import {ChangeDetectionStrategy, Component, EventEmitter, Input, Output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {NzCollapseModule} from 'ng-zorro-antd/collapse';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzSpinModule} from 'ng-zorro-antd/spin';

export interface DocumentSectionFile {
  fileName: string;
  fileType: string;
  url: string;
  s3Key?: string;
  sourceS3Key?: string;
}

const IMAGE_FILE_TYPES = new Set(['jpg', 'jpeg', 'png']);

export function getDocumentSectionFileType(file: Partial<DocumentSectionFile> | null | undefined): string {
  const fileType = (file?.fileType || '').toLowerCase().trim();
  if (fileType) {
    return fileType;
  }

  const fileName = (file?.fileName || file?.url || '').toLowerCase();
  const cleanName = fileName.split('?')[0].split('#')[0];
  const extension = cleanName.includes('.') ? cleanName.split('.').pop() || '' : '';
  return extension.trim();
}

export function isDocumentSectionImageFile(file: Partial<DocumentSectionFile> | null | undefined): boolean {
  return IMAGE_FILE_TYPES.has(getDocumentSectionFileType(file));
}

export function openDocumentSectionFile(
  file: Partial<DocumentSectionFile> | null | undefined,
  openImagePreview: (url: string) => void,
): void {
  const url = file?.url?.trim();
  if (!url) {
    return;
  }

  if (isDocumentSectionImageFile(file)) {
    openImagePreview(url);
    return;
  }

  window.open(url, '_blank', 'noopener');
}

export interface DocumentSection {
  title: string;
  translatedTitle?: string;
  files: DocumentSectionFile[];
}

export type DocumentSectionVariant = 'hospitality' | 'emergency';

@Component({
  selector: 'app-document-sections',
  standalone: true,
  imports: [CommonModule, TranslateModule, NzCollapseModule, NzIconModule, NzSpinModule],
  templateUrl: './document-sections.component.html',
  styleUrls: ['./document-sections.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocumentSectionsComponent {
  @Input() sections: DocumentSection[] = [];
  @Input() themeColor?: string | null;
  @Input() loading = false;
  @Input() headingKey = 'COMPANY_PAGE.DOCUMENTS';
  @Input() variant: DocumentSectionVariant = 'hospitality';

  @Output() sectionView = new EventEmitter<string>();
  @Output() documentView = new EventEmitter<{ sectionTitle: string; fileName: string }>();
  @Output() imagePreview = new EventEmitter<string>();

  get hasSections(): boolean {
    return Array.isArray(this.sections) && this.sections.length > 0;
  }

  get resolvedThemeColor(): string | null {
    return this.themeColor || null;
  }

  get variantClass(): string {
    return this.variant === 'emergency'
      ? 'documents-section--emergency'
      : 'documents-section--hospitality';
  }

  onPanelActiveChange(sectionTitle: string, isActive: boolean): void {
    if (isActive) {
      this.sectionView.emit(sectionTitle);
    }
  }

  onImageClick(sectionTitle: string, fileName: string, url: string): void {
    this.documentView.emit({ sectionTitle, fileName });
    this.imagePreview.emit(url);
  }

  onFileClick(sectionTitle: string, fileName: string): void {
    this.documentView.emit({ sectionTitle, fileName });
  }

  onDocItemClick(event: Event, sectionTitle: string, file: DocumentSectionFile): void {
    // If user clicked the actual <a> link (or something inside it), let that handler do its job
    const target = event.target as HTMLElement | null;
    if (target?.closest('a')) return;

    if (isDocumentSectionImageFile(file)) {
      return;
    }

    this.documentView.emit({ sectionTitle, fileName: file.fileName });
    openDocumentSectionFile(file, (url) => this.imagePreview.emit(url));
  }

  getFileType(file: DocumentSectionFile): string {
    return getDocumentSectionFileType(file);
  }
}
